/* ============================================================
   Brick Box — scroll motion layer (GSAP + ScrollTrigger)

   The design drives two effects from scroll position using CSS
   scroll-driven animations (`animation-timeline: view()`):

     tilein  .ctile / .bnr-tile / .t4v   y 52px -> 0, fade in,
                                         cover 2% -> 32%, 55ms stagger
     lineup  .stmt span                  #CFCFCB -> --ink, .5 -> 1 opacity,
                                         cover 12% -> 46%, scrubbed

   `view()` only ships in Chromium, so Safari and Firefox fell back to a
   class-toggling observer that could not scrub. This file reproduces both
   with ScrollTrigger so every browser gets the same motion, and takes the
   CSS animation off the element (inline `animation: none`) so the two
   never drive the same property at once.

   Loops that do not depend on scroll position (marquee, pulse, rot,
   lightsweep) stay in CSS — GSAP would add nothing.
   ============================================================ */
(function brickboxMotion() {
  'use strict';

  if (!window.gsap || !window.ScrollTrigger) return;

  var gsap = window.gsap;
  gsap.registerPlugin(window.ScrollTrigger);

  var TILES = '.ctile, .bnr-tile, .t4v';
  var LINES = '.stmt span';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clearCssAnimation(el) {
    el.style.animation = 'none';
  }

  /* Reduced motion: show the finished state, bind nothing. */
  function settle() {
    gsap.utils.toArray(TILES).forEach(function (el) {
      clearCssAnimation(el);
      gsap.set(el, { opacity: 1, y: 0 });
    });
    gsap.utils.toArray(LINES).forEach(function (el) {
      clearCssAnimation(el);
      gsap.set(el, { opacity: 1, clearProps: 'color' });
    });
  }

  function build() {
    var tiles = gsap.utils.toArray(TILES);
    if (tiles.length) {
      tiles.forEach(clearCssAnimation);
      gsap.set(tiles, { opacity: 0, y: 52 });

      /* batch() groups tiles that enter together so a row staggers as one,
         the way the design's per-tile --i delay reads. */
      window.ScrollTrigger.batch(tiles, {
        start: 'top 92%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.62,
            ease: 'power3.out',
            stagger: 0.055,
            overwrite: true
          });
        }
      });
    }

    /* The statement lines scrub with scroll rather than firing once, so the
       colour tracks the reader instead of snapping. */
    var lines = gsap.utils.toArray(LINES);
    lines.forEach(function (line) {
      clearCssAnimation(line);
      var ink = getComputedStyle(document.documentElement)
        .getPropertyValue('--ink').trim() || '#16181B';
      /* An <em> inside carries its own colour (the blue word) — leave it. */
      gsap.fromTo(
        line,
        { color: '#CFCFCB', opacity: 0.5 },
        {
          color: ink,
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: line,
            start: 'top 88%',
            end: 'top 46%',
            scrub: true
          }
        }
      );
    });
  }

  function start() {
    if (reduce.matches) settle();
    else build();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Late images change element positions; recalculate once they land. */
  window.addEventListener('load', function () {
    window.ScrollTrigger.refresh();
  });

  /* A page opened in a background tab gets no animation frames, so GSAP's
     ticker is paused and nothing reveals. Recalculate when it is looked at. */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) window.ScrollTrigger.refresh();
  });

  if (reduce.addEventListener) {
    reduce.addEventListener('change', function () {
      window.ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
      start();
    });
  }
})();
